import _ from "lodash";
import React, { PureComponent } from "react";

import classes from "./TagFolders.scss";

import ScreenPanel from "../../ScreenPanel";

import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import I18nHOC from "UIComponents/I18nHOC";

import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";

import { getCourseFoldersFromCache } from "Journal/modules/JournalGraphqlHelpers";

import { getCourseFolders } from "Journal/modules/JournalQuery";

import { FolderFilled, TickCircleFilled } from "@toddle-edu/ds-icons";

import { Button, Checkbox, EmptyState, SearchInput } from "@toddle-edu/ds-web";
import { NoFoldersIllustration } from "@toddle-edu/ds-theme";
import classNames from "classnames";

const styles = {
  titleTextStyle: { fontSize: "2rem" },
  footerContainerStyle: { height: "96px" },
  containerStyle: { padding: "0 32px" },
  titleContainerStyle: { marginTop: "30px" },
};

const FooterComponent = React.memo(props => {
  const {
    onDoneClick,
    onCloseClick,
    selectedFolders,
    t,
    showNewDesign,
  } = props;
  const countString = t("common:folder_selected", {
    count: _.size(selectedFolders, 0),
  });
  return (
    <div className={classes.footerButtonContainer}>
      {!showNewDesign ? (
        <div className={classes.footerText}>{countString}</div>
      ) : null}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          ...(showNewDesign ? { marginLeft: "auto" } : {}),
        }}
      >
        <Button
          size={"large"}
          onClick={onCloseClick}
          variant="outlined-subtle"
          style={{ marginRight: "12px" }}
          testId="journal-postcreation-tagfolders-cancel"
        >
          {t("common:cancel")}
        </Button>
        <Button
          size={"large"}
          variant="progressive"
          onClick={onDoneClick}
          icon={showNewDesign ? <TickCircleFilled /> : null}
          testId="journal-postcreation-tagfolders-done"
        >
          {t("common:done")}
        </Button>
      </div>
    </div>
  );
});

FooterComponent.displayName = "TagFolders_FooterComponent";

const FoldersList = React.memo(props => {
  const {
    courseFolders,
    t,
    selectedFolders,
    onItemClicked,
    showNewDesign = false,
  } = props;

  const onFolderClicked = folder => {
    const result = _.cloneDeep(selectedFolders);

    if (_.find(selectedFolders, { id: folder.id })) {
      _.remove(result, item => item.id == folder.id);
    } else {
      result.push(folder);
    }

    onItemClicked(result);
  };

  const folders = selectedFolders;
  const folderItemClass = classNames({
    [classes.folderItem]: !showNewDesign,
    [classes.folderItemV2]: showNewDesign,
  });

  return (
    <div className={classes.listContainer}>
      {!_.isEmpty(courseFolders) ? (
        _.map(courseFolders, folder => {
          const isChecked = !!_.find(folders, { id: folder.id });
          return (
            <div
              key={folder.id}
              onClick={() => onFolderClicked(folder)}
              className={folderItemClass}
              data-test-id={"journal-postcreation-tagfolderitem-" + folder.id}
              data-is-selected={isChecked}
            >
              <div className={classes.folderContent}>
                <div className={classes.folderIcon}>
                  <FolderFilled style={{ color: folder.color }} size="small" />
                </div>
                <LinkWithTooltip
                  tooltip={folder.name}
                  href="#"
                  placement="top"
                  overlayStyle={{ maxWidth: 300 }}
                  linkStyles={{ alignSelf: "center" }}
                >
                  <div className={classes.folderName}>{folder.name}</div>
                </LinkWithTooltip>
              </div>
              <div className={classes.itemRightContainer}>
                <Checkbox isChecked={isChecked} />
              </div>
            </div>
          );
        })
      ) : (
        <div
          className={classes.emptyContainer}
          data-test-id={"journal-postcreation-taggedfolders-count"}
        >
          <EmptyState
            illustration={NoFoldersIllustration}
            title={t("journal:no_folders_tagged")}
            subtitle={t("journal:create_folder_to_tag")}
          />
        </div>
      )}
    </div>
  );
});

FoldersList.displayName = "FoldersList";

class TagFolders extends PureComponent {
  constructor(props) {
    super(props);

    const {
      screenPanelProps: {
        mode,
        postDetails: { folders, addedFolders, removedFolders },
      },
    } = this.props;

    let selectedFolders = [];

    //if new then don't do any add remove
    if (mode === "create") {
      selectedFolders = folders;
    }

    //if update then do add remove on folders and setState selectedFolders
    else {
      const selectedFoldersCopy = _.cloneDeep(folders) || [];
      const addedFoldersCopy = _.cloneDeep(addedFolders) || [];

      selectedFolders = _.uniqWith(
        [...selectedFoldersCopy, ...addedFoldersCopy],
        _.isEqual
      );

      _.remove(selectedFolders, folder => {
        return !!_.find(removedFolders, { id: folder.id });
      });
    }

    this.state = {
      selectedFolders,
      searchTerm: "",
    };
  }

  onDoneClick = () => {
    const {
      screenPanelProps: { onCloseClick, updateFolderArray } = {},
    } = this.props;

    const { selectedFolders, addedFolders, removedFolders } = this.state;

    updateFolderArray(selectedFolders);

    onCloseClick();
  };

  onItemClicked = data => {
    this.setState({ selectedFolders: data });
  };

  getMatchingFolders = (courseFolders, searchTerm) => {
    if (_.isEmpty(searchTerm)) return courseFolders;
    const filteredFolders = _.filter(courseFolders, folder =>
      _.includes(_.lowerCase(_.get(folder, "name")), _.lowerCase(searchTerm))
    );
    return filteredFolders;
  };

  render() {
    const {
      screenPanelProps,
      screenPanelProps: {
        mode,
        postDetails: { folders },
        showNewDesign = false,
      },
      courseFolders,
      t,
    } = this.props;

    const { selectedFolders, addedFolders, removedFolders } = this.state;

    return (
      <ScreenPanel
        {...screenPanelProps}
        footerComponent={
          !_.isEmpty(courseFolders) && (
            <FooterComponent
              t={t}
              onDoneClick={this.onDoneClick}
              onCloseClick={
                !_.isUndefined(screenPanelProps?.onCloseClick) &&
                screenPanelProps?.onCloseClick
              }
              courseFolders={courseFolders}
              selectedFolders={selectedFolders}
              showNewDesign={showNewDesign}
            />
          )
        }
        titleTextStyle={styles.titleTextStyle}
        footerContainerStyle={styles.footerContainerStyle}
        containerStyle={styles.containerStyle}
        titleContainerStyle={styles.titleContainerStyle}
      >
        <div className={classes.bodyContainer}>
          <div style={{ margin: "16px 0 20px 0" }}>
            <SearchInput
              value={_.get(this.state, "searchTerm", "")}
              onChange={value => this.setState({ searchTerm: value })}
              placeholder={t("common:search_for_a_with_label", {
                label: t("common:lowercase", { text: t("common:folder") }),
              })}
              size={"large"}
              testId="journal-postcreation-tagfolders-search"
            />
          </div>
          <FoldersList
            t={t}
            postCreationMode={mode}
            courseFolders={this.getMatchingFolders(
              courseFolders,
              this.state.searchTerm
            )}
            prevFolders={folders}
            selectedFolders={selectedFolders}
            addedFolders={addedFolders}
            removedFolders={removedFolders}
            onItemClicked={this.onItemClicked}
            showNewDesign={showNewDesign}
          />
        </div>
      </ScreenPanel>
    );
  }
}

const mapActionCreators = {};

const mapStateToProps = (state, ownProps) => {
  const courseIdFromParent = _.get(
    ownProps,
    "screenPanelProps.postDetails.courseId"
  );
  const organizationId = state.login.userInfo.org_id;
  const courseId = courseIdFromParent
    ? courseIdFromParent
    : state.teacher.selected_class.selected_course;

  return {
    courseId,
    organizationId,
  };
};

export default compose(
  I18nHOC({ resource: ["journal", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getCourseFolders, {
    name: "getCourseFolders",
    options: ({ courseId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        courseId: courseId,
      },
    }),
    props({ getCourseFolders, ownProps: { courseId } }) {
      const data = getCourseFoldersFromCache(courseId);

      return {
        isData: !_.isEmpty(data),
        courseFolders: _.get(data, "folders", []),
        networkStatus: getCourseFolders.networkStatus,
        isLoading:
          getCourseFolders["networkStatus"] == 1 ||
          getCourseFolders["networkStatus"] == 2,
      };
    },
  })
)(TagFolders);
